import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Settings, ChevronRight } from "lucide-react";
import useFinance from "../hooks/useFinance";
import GlobalPermissions from "../components/Finance/PermissionManagement/GlobalPermissions";
import TableList from "../components/Finance/Tables/TableList";
import CreateTableModal from "../components/Finance/Modals/CreateTableModal";
import TemplateModal from "../components/Finance/Modals/TemplateModal";
import LoadingSpinner from "../components/Finance/Common/LoadingSpinner";
import MainHeader from "../MainHeader";

export default function FinancePage() {
  const navigate = useNavigate();
  const {
    fetchGlobalPermissions,
    fetchTableList,
    createTable,
    createTableFromTemplate,
  } = useFinance();

  // States
  const [loading, setLoading] = useState(true);
  const [globalPermissions, setGlobalPermissions] = useState([]);
  const [tables, setTables] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showGlobalPermissions, setShowGlobalPermissions] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [refreshKey]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load tables
      const tableResult = await fetchTableList();
      if (tableResult.ok) {
        setTables(tableResult.tables);
        setTotalCount(tableResult.total_count);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalPermissions = async () => {
    const permResult = await fetchGlobalPermissions();
    if (permResult.ok) {
      setGlobalPermissions(permResult.permissions);
    } else if (permResult.denied) {
      window.showToast?.("권한 조회 권한이 없습니다.", "error");
    }
  };

  const handleCreateTable = async (data) => {
    const result = await createTable(data);
    if (result.denied) {
      window.showToast?.("테이블 생성 권한이 없습니다.", "error");
    } else if (result.ok) {
      setRefreshKey((k) => k + 1);
      setShowCreateModal(false);
      window.showToast?.("테이블이 생성되었습니다.", "success");
    } else {
      window.showToast?.("테이블 생성에 실패했습니다.", "error");
    }
  };

  const handleCreateFromTemplate = async (data) => {
    const result = await createTableFromTemplate(data);
    if (result.denied) {
      window.showToast?.("테이블 생성 권한이 없습니다.", "error");
    } else if (result.ok) {
      setRefreshKey((k) => k + 1);
      setShowTemplateModal(false);
      window.showToast?.("템플릿에서 테이블이 생성되었습니다.", "success");
    } else {
      window.showToast?.("테이블 생성에 실패했습니다.", "error");
    }
  };

  const handleTableClick = (tableId) => {
    navigate(`/financial_management/tableView/${tableId}`);
  };

  const handleShowGlobalPermissions = async () => {
    await loadGlobalPermissions();
    setShowGlobalPermissions(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">재무 관리</h1>
                <ChevronRight className="w-5 h-5 text-gray-400" />
                <span className="text-lg text-gray-600">테이블 목록</span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleShowGlobalPermissions}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-sm font-medium">전체 권한 관리</span>
                </button>

                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">템플릿에서 생성</span>
                </button>

                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-sm transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">새 테이블</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">
                  {tables.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">전체 테이블</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">
                  {tables.filter((t) => t.permission_level === "owner").length}
                </p>
                <p className="text-sm text-gray-600 mt-1">내가 생성한 테이블</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {
                    tables.filter((t) =>
                      ["admin", "edit"].includes(t.permission_level)
                    ).length
                  }
                </p>
                <p className="text-sm text-gray-600 mt-1">편집 가능한 테이블</p>
              </div>
            </div>
          </div>
        </div>

        {/* Table List */}
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <TableList
            tables={tables}
            onTableClick={handleTableClick}
            onRefresh={() => setRefreshKey((k) => k + 1)}
          />
        </div>

        {/* Modals */}
        {showCreateModal && (
          <CreateTableModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateTable}
          />
        )}

        {showTemplateModal && (
          <TemplateModal
            isOpen={showTemplateModal}
            onClose={() => setShowTemplateModal(false)}
            onSubmit={handleCreateFromTemplate}
          />
        )}

        {showGlobalPermissions && (
          <GlobalPermissions
            isOpen={showGlobalPermissions}
            onClose={() => setShowGlobalPermissions(false)}
            permissions={globalPermissions}
            onUpdate={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </div>
    </>
  );
}
